/**
 * Cognito OAuth 2.0 provider implementation.
 */

import { IOAuthProvider, OAuthProviderConfig, OAuthTokenResponse, StoredTokens } from './types'

export class CognitoOAuthProvider implements IOAuthProvider {
    public buildAuthUrl (
        config: OAuthProviderConfig,
        codeChallenge: string,
        redirectUri: string,
        state: string,
    ): string {
        const params = new URLSearchParams({
            client_id: config.clientId,
            response_type: 'code',
            scope: 'openid profile email',
            redirect_uri: redirectUri,
            code_challenge: codeChallenge,
            code_challenge_method: 'S256',
            state,
            // Ask the Cognito Hosted UI to re-authenticate even when its session
            // cookie is still present. Defense-in-depth only: the Hosted UI does
            // not always honor `prompt=login`, so sign-out primarily clears the
            // Cognito session cookie via a top-level /logout redirect (see
            // AuthService.logout / buildCognitoLogoutUrl, P494835427). This keeps
            // re-prompting if that cookie clear is ever missed.
            prompt: 'login',
        })
        return `${config.authorizationEndpoint}?${params.toString()}`
    }

    public async exchangeCodeForTokens (
        config: OAuthProviderConfig,
        code: string,
        codeVerifier: string,
        redirectUri: string,
    ): Promise<StoredTokens> {
        const body = new URLSearchParams({
            grant_type: 'authorization_code',
            client_id: config.clientId,
            code,
            redirect_uri: redirectUri,
            code_verifier: codeVerifier,
        })

        const response = await fetch(config.tokenEndpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body: body.toString(),
        })

        if (!response.ok) {
            const text = await response.text()
            throw new Error(`Token exchange failed (${response.status}): ${text}`)
        }

        const data: OAuthTokenResponse = await response.json()
        const expiresAt = Date.now() + data.expires_in * 1000

        return {
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
            idToken: data.id_token,
            expiresIn: data.expires_in,
            expiresAt,
        }
    }
}

export const cognitoProvider = new CognitoOAuthProvider()
